export const ONBOARDING_WORK_OPTIONS = [
  { value: 'employed', emoji: '🏢', label: 'Employed', note: 'We’ll keep payslips, rights, sick pay, contracts, and work admin closer to hand.' },
  { value: 'self-employed', emoji: '💼', label: 'Self-employed or freelance', note: 'We’ll keep HMRC, expenses, invoicing, and money admin closer to hand.' },
  { value: 'mix', emoji: '🔀', label: 'Mix of both', note: 'We’ll keep both employee and self-employed tools nearby, without making you force one lane.' },
  { value: 'not-working', emoji: '🏠', label: 'Not currently working', note: 'We’ll keep Work quieter for now and let the rest of Adulting lead.' },
] as const;

export const ONBOARDING_DEPENDANT_OPTIONS = [
  { value: 'children', emoji: '👶', label: 'Children' },
  { value: 'elderly', emoji: '👴', label: 'Older family member' },
  { value: 'disabled', emoji: '🩺', label: 'Disabled or unwell family member' },
] as const;

export const ONBOARDING_TENURE_OPTIONS = [
  { value: 'renting-private', emoji: '🏠', label: 'Renting privately' },
  { value: 'renting-social', emoji: '🏢', label: 'Renting social' },
  { value: 'owner-occupier', emoji: '🔑', label: 'Owner-occupier' },
  { value: 'living-with-family', emoji: '👨‍👩‍👧', label: 'Living with family' },
  { value: 'other', emoji: '📋', label: 'Other' },
] as const;

export const ONBOARDING_COUNCIL_TAX_BANDS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'] as const;

export const ONBOARDING_INTEGRATION_INTENT_OPTIONS = [
  {
    value: 'connect-now',
    label: 'Set up now',
    note: 'Adulting can help you connect this straight after the basics.',
  },
  {
    value: 'add-later',
    label: 'Do later in My Settings',
    note: 'Keep moving for now and come back from My Settings when you are ready.',
  },
  {
    value: 'not-now',
    label: 'Keep it in Adulting',
    note: 'No pressure. Adulting can keep this securely managed inside Adulting across your Adulting devices for now.',
  },
] as const;

export const ONBOARDING_WELCOME_TITLE = 'Welcome - let’s get Adulting ready around your life';
export const ONBOARDING_COMPLETE_TITLE = 'You’re set up - Adulting is getting ready around you';
export const ONBOARDING_DATA_PROMISE =
  'We only use this information to personalise Adulting for you and to make the app work properly. It is never sold on or made available for another third party’s own purposes.';
export const ONBOARDING_WELCOME_BODY_PRIMARY =
  'Adulting is here to make life admin feel lighter, calmer, and easier to stay on top of.';
export const ONBOARDING_WELCOME_BODY_SECONDARY =
  'Do as much or as little as you have energy for today. Adulting will save the useful basics now and keep anything you skip easy to find later in My Settings.';
export const ONBOARDING_STEP_SUPPORT_NOTE =
  'A simple yes or no is enough in most places. Add extra detail only where it will genuinely help Adulting feel more ready for you.';
export const ONBOARDING_CONNECTIONS_NOTE =
  'Banking, calendar, and documents are all optional here. Adulting can keep them securely managed and in sync across your Adulting devices on its own, connect to your own services for speed, or use a mix of both. Every choice stays easy to change later in My Settings.';
export const ONBOARDING_HOME_ROUTINES_NOTE =
  'If these are part of your home life, Adulting can keep the right little nudges visible without you having to remember them all yourself. Leave them blank and decide later in My Settings if that is easier today.';
export const ONBOARDING_STEP_SEQUENCE = [
  { step: 1, label: 'About you', hint: 'Basics and work setup' },
  { step: 2, label: 'People', hint: 'Partner, children, and dependants' },
  { step: 3, label: 'Pets', hint: 'Only if they matter to your admin' },
  { step: 4, label: 'Home', hint: 'Address, school, and vehicle context' },
  { step: 5, label: 'Connections', hint: 'Banking, files, and calendar' },
] as const;

export const ONBOARDING_STEP_COPY = {
  1: {
    title: 'Start with the basics',
    body: 'A few basics help Adulting bring the right sections forward without making you hunt around later.',
  },
  2: {
    title: 'Who else should Adulting keep in mind?',
    body: 'Partner, children, and dependant details help Adulting bring family admin, school, and care load forward from the start.',
  },
  3: {
    title: 'Any pets or pet admin?',
    body: 'Only add these if they matter. Pet details help Adulting keep care reminders, paperwork, and health admin together when you need them.',
  },
  4: {
    title: 'What should Home already know?',
    body: 'Address, school confirmation, council tax, and vehicle details help Adulting pull in the right home context instead of asking again later.',
  },
  5: {
    title: 'What should Adulting help connect?',
    body: 'Choose what should stay managed inside Adulting, what should connect to your own services, and what can wait until later in My Settings.',
  },
} as const;

export type OnboardingIntegrationIntent = (typeof ONBOARDING_INTEGRATION_INTENT_OPTIONS)[number]['value'];
export type OnboardingYesNo = '' | 'yes' | 'no';
export type OnboardingAdultDependantKind = 'elderly' | 'disabled';

export type OnboardingChildDraft = {
  id: string;
  name: string;
  dateOfBirth: string;
  schoolName: string;
  schoolUrn: string;
};

export type OnboardingAdultDependantDraft = {
  id: string;
  name: string;
  dateOfBirth: string;
  kind: OnboardingAdultDependantKind;
};

export type OnboardingPetDraft = {
  id: string;
  name: string;
  petType: string;
  dateOfBirth: string;
};

export function buildOnboardingDraftId(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createOnboardingChildDraft(): OnboardingChildDraft {
  return {
    id: buildOnboardingDraftId('child'),
    name: '',
    dateOfBirth: '',
    schoolName: '',
    schoolUrn: '',
  };
}

export function createOnboardingAdultDependantDraft(
  kind: OnboardingAdultDependantKind = 'elderly',
): OnboardingAdultDependantDraft {
  return {
    id: buildOnboardingDraftId('adult'),
    name: '',
    dateOfBirth: '',
    kind,
  };
}

export function createOnboardingPetDraft(): OnboardingPetDraft {
  return {
    id: buildOnboardingDraftId('pet'),
    name: '',
    petType: 'dog',
    dateOfBirth: '',
  };
}
